import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { channels, site } from "@/data/enrichments";
import EmailCapture from "@/components/EmailCapture";

const TOP_CHANNELS = [
  "instagram",
  "youtube-epics",
  "spotify",
  "apple-music",
  "substack",
];

export default function Footer() {
  const featured = TOP_CHANNELS.map((id) =>
    channels.find((c) => c.id === id)
  ).filter(Boolean);

  return (
    <footer className="relative border-t border-dharma-gold/20 bg-parchment/40 mt-20">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 py-14">
        {/* Newsletter band — site-wide signup. The book-page EmailCapture is
            still the higher-intent placement (per-title context); this footer
            slot catches everyone else (homepage / books index / songs / about). */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center pb-12">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">Stay in touch</div>
            <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish">
              When the next one is ready.
            </h3>
          </div>
          <div className="md:col-span-7">
            <EmailCapture
              titleId="footer"
              slug="footer"
              prompt="New book. New song. New film. Nothing else."
            />
          </div>
        </div>

        <div className="rule-gold mb-12" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <img
              src={site.logo_lockup}
              alt="DharmaWeave"
              className="h-10 md:h-11 w-auto mb-5 select-none"
              draggable={false}
            />
            <p className="font-serif text-lg italic text-ink-soft leading-relaxed max-w-xs">
              Sacred stories, woven with light.
            </p>
            <p className="font-body text-xs text-ink-mute mt-4 max-w-xs leading-relaxed">
              Graphic novels, songs, and films from the world's sacred
              traditions.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow mb-3">Visit</div>
            <ul className="space-y-2 font-serif text-lg text-ink-soft">
              <li><Link to="/books" className="hover:text-dharma-gold-deep transition-colors">Books</Link></li>
              <li><Link to="/writing" className="hover:text-dharma-gold-deep transition-colors">Writing</Link></li>
              <li><Link to="/listen" className="hover:text-dharma-gold-deep transition-colors">Listen</Link></li>
              <li><Link to="/watch" className="hover:text-dharma-gold-deep transition-colors">Films</Link></li>
              <li><Link to="/follow" className="hover:text-dharma-gold-deep transition-colors">Follow</Link></li>
              <li><Link to="/about" className="hover:text-dharma-gold-deep transition-colors">About</Link></li>
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="eyebrow mb-3">Find us elsewhere</div>
            <ul className="space-y-2.5 font-body text-sm">
              {featured.map((c) => (
                <li key={c.id}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-ink-soft hover:text-dharma-gold-deep transition-colors group"
                  >
                    <span className="font-serif text-lg">{c.label}</span>
                    <span className="text-ink-mute">·</span>
                    <span className="text-ink-mute">{c.handle}</span>
                    <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/follow"
                  className="font-body text-xs tracking-widest2 uppercase text-dharma-gold-deep hover:text-saffron-fire transition-colors"
                >
                  Every channel →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="rule-gold mt-12 mb-6" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="font-body text-xs text-ink-mute">
            © {new Date().getFullYear()} {site.name} · A{" "}
            <a
              href={site.parent.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-dharma-gold-deep hover:text-saffron-fire transition-colors"
            >
              Tara Ventures
            </a>{" "}
            brand
          </p>
          <a
            href={`mailto:${site.contact_email}`}
            className="font-body text-xs text-ink-mute hover:text-dharma-gold-deep transition-colors"
          >
            {site.contact_email}
          </a>
        </div>
      </div>
    </footer>
  );
}
